import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Vehicle } from "../../../../api/vehiclesApi";
import { clearClusterer, createClusterer } from "../../utils/clusterUtils";
import { debounce } from "../../utils/debounce";
import {
  calculateThreshold,
  getLabelPosition,
} from "../../utils/labelPositioning";
import { createMarkerElement, LabelPosition } from "../../utils/markerUtils";

const GOOGLE_MAPS_LIBRARIES = ["marker"] as const;

interface UseVehicleMarkersProps {
  map: google.maps.Map | null;
  vehicles: Vehicle[];
  selectedVehicleId?: number;
  onVehicleClick?: (vehicleId: number) => void;
  clustersEnabled?: boolean;
  isVisible?: boolean;
}

interface MarkerAnimation {
  marker: google.maps.marker.AdvancedMarkerElement;
  startPos: google.maps.LatLngLiteral;
  endPos: google.maps.LatLngLiteral;
  startTime: number;
  animationFrame?: number;
}

const ANIMATION_DURATION = 10000;
const MIN_POSITION_CHANGE = 0.0005;

export const useVehicleMarkers = ({
  map,
  vehicles,
  selectedVehicleId,
  onVehicleClick,
  clustersEnabled = false,
  isVisible = true,
}: UseVehicleMarkersProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const markersRef = useRef<
    Map<number, google.maps.marker.AdvancedMarkerElement>
  >(new Map());
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const previousPositionsRef = useRef<Map<number, google.maps.LatLngLiteral>>(
    new Map()
  );
  const activeAnimationsRef = useRef<Map<number, MarkerAnimation>>(new Map());
  const isFirstRenderRef = useRef(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const vehiclesRef = useRef<Vehicle[]>([]);
  const markerLibraryRef = useRef<
    typeof google.maps.marker.AdvancedMarkerElement | null
  >(null);

  // Function for loading Google Maps library
  const loadMarkerLibrary = useCallback(async () => {
    if (!markerLibraryRef.current) {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        "marker"
      )) as google.maps.MarkerLibrary;
      markerLibraryRef.current = AdvancedMarkerElement;
    }
    return markerLibraryRef.current;
  }, []);

  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  const isSignificantChange = (
    pos1: google.maps.LatLngLiteral,
    pos2: google.maps.LatLngLiteral
  ): boolean => {
    const latDiff = Math.abs(pos1.lat - pos2.lat);
    const lngDiff = Math.abs(pos1.lng - pos2.lng);
    return latDiff > MIN_POSITION_CHANGE || lngDiff > MIN_POSITION_CHANGE;
  };

  const animateMarker = (
    vehicleId: number,
    marker: google.maps.marker.AdvancedMarkerElement,
    startPos: google.maps.LatLngLiteral,
    endPos: google.maps.LatLngLiteral
  ) => {
    const existingAnimation = activeAnimationsRef.current.get(vehicleId);
    if (existingAnimation?.animationFrame) {
      cancelAnimationFrame(existingAnimation.animationFrame);
    }

    const startTime = performance.now();

    const animation: MarkerAnimation = {
      marker,
      startPos,
      endPos,
      startTime,
    };

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const easeProgress = easeInOutCubic(progress);

      if (!activeAnimationsRef.current.has(vehicleId)) {
        return;
      }

      const newPos = {
        lat: startPos.lat + (endPos.lat - startPos.lat) * easeProgress,
        lng: startPos.lng + (endPos.lng - startPos.lng) * easeProgress,
      };

      marker.position = newPos;

      previousPositionsRef.current.set(vehicleId, newPos);

      if (progress < 1) {
        animation.animationFrame = requestAnimationFrame(animate);
      } else {
        marker.position = endPos;
        activeAnimationsRef.current.delete(vehicleId);
        previousPositionsRef.current.set(vehicleId, endPos);
        animation.animationFrame = undefined;
      }
    };

    animation.animationFrame = requestAnimationFrame(animate);
    activeAnimationsRef.current.set(vehicleId, animation);
  };

  const createOrUpdateMarker = useCallback(
    async (
      vehicle: Vehicle,
      newPosition: google.maps.LatLngLiteral,
      labelPosition: LabelPosition,
      AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement
    ) => {
      const existingMarker = markersRef.current.get(vehicle.id);
      const markerElement = createMarkerElement({
        vehicle,
        isSelected: vehicle.id === selectedVehicleId,
        labelPosition,
        isDarkMode,
      });

      let marker: google.maps.marker.AdvancedMarkerElement;

      if (existingMarker) {
        existingMarker.content = markerElement;
        marker = existingMarker;

        const previousPosition = previousPositionsRef.current.get(vehicle.id);

        if (isFirstRenderRef.current) {
          marker.position = newPosition;
          previousPositionsRef.current.set(vehicle.id, newPosition);
        } else if (previousPosition) {
          animateMarker(vehicle.id, marker, previousPosition, newPosition);
        } else {
          marker.position = newPosition;
          previousPositionsRef.current.set(vehicle.id, newPosition);
        }
      } else {
        marker = new AdvancedMarkerElement({
          position: newPosition,
          content: markerElement,
          collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
        });

        marker.addListener("gmp-click", () => {
          onVehicleClick?.(vehicle.id);
        });

        markersRef.current.set(vehicle.id, marker);
        previousPositionsRef.current.set(vehicle.id, newPosition);
      }

      return marker;
    },
    [selectedVehicleId, onVehicleClick, isDarkMode]
  );

  const updateMarkers = useCallback(async () => {
    if (!map || !isMapReady) {
      return;
    }

    vehiclesRef.current = vehicles;

    if (!isVisible) {
      if (clustererRef.current) {
        clearClusterer(clustererRef.current);
        clustererRef.current = null;
      }

      for (const animation of activeAnimationsRef.current.values()) {
        if (animation.animationFrame) {
          cancelAnimationFrame(animation.animationFrame);
        }
      }
      activeAnimationsRef.current.clear();

      markersRef.current.forEach((marker) => {
        marker.map = null;
      });
      previousPositionsRef.current.clear();
      return;
    }

    const AdvancedMarkerElement = await loadMarkerLibrary();
    if (!AdvancedMarkerElement) {
      return;
    }

    const currentZoom = map.getZoom() || 15;
    const threshold = calculateThreshold(currentZoom);

    const existingMarkers = new Map(markersRef.current);

    for (const [id, marker] of existingMarkers.entries()) {
      if (!vehicles.find((v) => v.id === id)) {
        if (clustererRef.current) {
          clustererRef.current.removeMarker(marker);
        }
        const animation = activeAnimationsRef.current.get(id);
        if (animation?.animationFrame) {
          cancelAnimationFrame(animation.animationFrame);
        }
        activeAnimationsRef.current.delete(id);
        marker.map = null;
        markersRef.current.delete(id);
        previousPositionsRef.current.delete(id);
      }
    }
    if (clustererRef.current) {
      clearClusterer(clustererRef.current);
      clustererRef.current = null;
    }

    const occupiedPositions = new Map<number, LabelPosition>();
    const currentMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

    for (let i = 0; i < vehicles.length; i++) {
      const vehicle = vehicles[i];
      if (vehicle.lastLatitude == null || vehicle.lastLongitude == null) {
        const existingWithoutLocation = markersRef.current.get(vehicle.id);
        if (existingWithoutLocation) {
          existingWithoutLocation.map = null;
          markersRef.current.delete(vehicle.id);
          previousPositionsRef.current.delete(vehicle.id);
        }
        continue;
      }

      const newPosition = {
        lat: vehicle.lastLatitude,
        lng: vehicle.lastLongitude,
      };

      const labelPosition = getLabelPosition(
        vehicle,
        vehicles,
        occupiedPositions,
        threshold
      );
      occupiedPositions.set(vehicle.id, labelPosition);

      const marker = await createOrUpdateMarker(
        vehicle,
        newPosition,
        labelPosition,
        AdvancedMarkerElement
      );

      if (clustersEnabled) {
        currentMarkers.push(marker);
        marker.map = null;
      } else {
        marker.map = map;
      }
    }

    if (clustersEnabled && currentMarkers.length > 0) {
      clustererRef.current = createClusterer({
        map,
        markers: currentMarkers,
      });
    }

    isFirstRenderRef.current = false;
  }, [
    map,
    vehicles,
    selectedVehicleId,
    onVehicleClick,
    clustersEnabled,
    isVisible,
    isMapReady,
    createOrUpdateMarker,
    loadMarkerLibrary,
  ]);

  useEffect(() => {
    if (map) {
      setIsMapReady(true);
    }
  }, [map]);

  useEffect(() => {
    if (!map || !isMapReady) return;

    const debouncedUpdate = debounce(updateMarkers, 300);

    updateMarkers();

    const zoomListener = map.addListener("zoom_changed", debouncedUpdate);

    return () => {
      for (const animation of activeAnimationsRef.current.values()) {
        if (animation.animationFrame) {
          cancelAnimationFrame(animation.animationFrame);
        }
      }
      activeAnimationsRef.current.clear();

      if (clustererRef.current) {
        clearClusterer(clustererRef.current);
      }

      markersRef.current.forEach((marker) => {
        marker.map = null;
      });

      google.maps.event.removeListener(zoomListener);
    };
  }, [map, updateMarkers, isMapReady]);
};
